import { findKey } from 'lodash-es';

import { useTheme } from '@mui/material';

import {
  CampaignStageType,
  CampaignStatusGroupType,
  ContactSegmentGroupStatus,
  ExperimentStatus,
} from '@/generated';

export enum CampaignStageGroupEnumType {
  Draft = 'DRAFT',
  Proofing = 'PROOFING',
  Preparing = 'PREPARING',
  Scheduled = 'SCHEDULED',
  InProgress = 'IN_PROGRESS',
  Suspended = 'SUSPENDED',
  Completed = 'COMPLETED',
  Canceled = 'CANCELED',
  Terminated = 'TERMINATED',
}

export const stageGroupMapping: Record<
  CampaignStageGroupEnumType,
  CampaignStageType[]
> = {
  [CampaignStageGroupEnumType.Draft]: [CampaignStageType.Draft],
  [CampaignStageGroupEnumType.Proofing]: [
    CampaignStageType.Proofing,
    CampaignStageType.Syncing,
    CampaignStageType.ReadyToProof,
    CampaignStageType.PreparingProofs,
    CampaignStageType.ReadyToExportProofs,
    CampaignStageType.ExportingProofs,
    CampaignStageType.Proofed,
    CampaignStageType.ReadyToPrepareProofs,
  ],
  [CampaignStageGroupEnumType.Preparing]: [
    CampaignStageType.ReadyToPrepare,
    CampaignStageType.Preparing,
    CampaignStageType.Prepared,
    CampaignStageType.ReadyToExport,
    CampaignStageType.Exporting,
    CampaignStageType.ReadyToSchedule,
  ],
  [CampaignStageGroupEnumType.Scheduled]: [
    CampaignStageType.Scheduling,
    CampaignStageType.Scheduled,
    CampaignStageType.Unscheduling,
  ],
  [CampaignStageGroupEnumType.InProgress]: [
    CampaignStageType.Queuing,
    CampaignStageType.ReadyToSend,
    CampaignStageType.Sending,
  ],
  [CampaignStageGroupEnumType.Suspended]: [
    CampaignStageType.Suspending,
    CampaignStageType.Suspended,
    CampaignStageType.Resuming,
  ],
  [CampaignStageGroupEnumType.Completed]: [CampaignStageType.Completed],
  [CampaignStageGroupEnumType.Canceled]: [
    CampaignStageType.Canceled,
    CampaignStageType.Canceling,
    CampaignStageType.Terminating,
    CampaignStageType.Terminated,
    CampaignStageType.Failed,
  ],
  [CampaignStageGroupEnumType.Terminated]: [
    CampaignStageType.Terminated,
    CampaignStageType.Terminating,
  ],
};

/**
 * Encode grouped stage-filter values (the UI representation) into the flat list
 * of raw `CampaignStageType` values the analytics query + saved-filter storage
 * use. Inverse of {@link stagesToCampaignStageGroups}; the pair MUST be used
 * together so the save/hydrate round-trip is symmetric.
 */
export const campaignStageGroupsToStages = (
  groups: CampaignStageGroupEnumType[],
): CampaignStageType[] =>
  groups.flatMap((group) => stageGroupMapping[group] ?? []);

/** Decode raw `CampaignStageType` values back into grouped stage-filter values. */
export const stagesToCampaignStageGroups = (
  stages: string[],
): CampaignStageGroupEnumType[] => {
  const groups = new Set<CampaignStageGroupEnumType>();
  stages.forEach((stage) => {
    (
      Object.entries(stageGroupMapping) as [
        CampaignStageGroupEnumType,
        CampaignStageType[],
      ][]
    ).forEach(([group, groupStages]) => {
      if (groupStages.map(String).includes(stage)) {
        groups.add(group);
      }
    });
  });
  return Array.from(groups);
};

export const labels: {
  [key in CampaignStageGroupEnumType]?: string;
} = {
  [CampaignStageGroupEnumType.Draft]: 'Draft',
  [CampaignStageGroupEnumType.Proofing]: 'Proofing',
  [CampaignStageGroupEnumType.Preparing]: 'Preparing',
  [CampaignStageGroupEnumType.Scheduled]: 'Scheduled',
  [CampaignStageGroupEnumType.InProgress]: 'In Progress',
  [CampaignStageGroupEnumType.Suspended]: 'Suspended',
  [CampaignStageGroupEnumType.Completed]: 'Completed',
  [CampaignStageGroupEnumType.Canceled]: 'Canceled',
  [CampaignStageGroupEnumType.Terminated]: 'Terminated',
};

export const statusGroupLabels: Record<CampaignStatusGroupType, string> = {
  [CampaignStatusGroupType.Draft]: 'Draft',
  [CampaignStatusGroupType.Proofing]: 'Proofing',
  [CampaignStatusGroupType.Preparing]: 'Preparing',
  [CampaignStatusGroupType.Scheduled]: 'Scheduled',
  [CampaignStatusGroupType.InProgress]: 'In Progress',
  [CampaignStatusGroupType.Suspended]: 'Suspended',
  [CampaignStatusGroupType.Completed]: 'Completed',
  [CampaignStatusGroupType.Canceled]: 'Canceled',
  [CampaignStatusGroupType.Active]: 'Active',
};
export const subStageLabels: { [key in CampaignStageType]?: string } = {
  [CampaignStageType.Draft]: 'Draft',
  [CampaignStageType.Syncing]: 'Syncing',
  [CampaignStageType.ReadyToPrepareProofs]: 'Ready to prepare proofs',
  [CampaignStageType.PreparingProofs]: 'Preparing proofs',
  [CampaignStageType.ReadyToExportProofs]: 'Ready to export proofs',
  [CampaignStageType.ExportingProofs]: 'Exporting proofs',
  [CampaignStageType.ReadyToProof]: 'Ready to proof',
  [CampaignStageType.Proofing]: 'Proofing',
  [CampaignStageType.Proofed]: 'Proofed',
  [CampaignStageType.ReadyToPrepare]: 'Ready to prepare',
  [CampaignStageType.Preparing]: 'Preparing',
  [CampaignStageType.Prepared]: 'Prepared',
  [CampaignStageType.ReadyToExport]: 'Ready to export',
  [CampaignStageType.Exporting]: 'Exporting',
  [CampaignStageType.ReadyToSchedule]: 'Ready to schedule',
  [CampaignStageType.Scheduling]: 'Scheduling',
  [CampaignStageType.Scheduled]: 'Scheduled',
  [CampaignStageType.Unscheduling]: 'Unscheduling',
  [CampaignStageType.Queuing]: 'Queueing',
  [CampaignStageType.ReadyToSend]: 'Ready to send',
  [CampaignStageType.Sending]: 'Sending',
  [CampaignStageType.Suspending]: 'Suspending',
  [CampaignStageType.Suspended]: 'Suspended',
  [CampaignStageType.Resuming]: 'Resuming',
  [CampaignStageType.Completed]: 'Completed',
  [CampaignStageType.Canceling]: 'Canceling',
  [CampaignStageType.Canceled]: 'Canceled',
  [CampaignStageType.Terminating]: 'Terminating',
  [CampaignStageType.Terminated]: 'Terminated',
  [CampaignStageType.Failed]: 'Failed',
};

const useStageHelper = () => {
  const theme = useTheme();
  const campaignStageColors: {
    [key in CampaignStageGroupEnumType]?: {
      text: string;
      border: string;
      background: string;
      pillBackground: string;
      pillText: string;
    };
  } = {
    [CampaignStageGroupEnumType.Draft]: {
      text: theme.palette.neutral700,
      border: theme.palette.neutral700,
      background: theme.palette.neutral700,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.neutral700,
    },
    [CampaignStageGroupEnumType.Proofing]: {
      text: theme.palette.secondary.main,
      border: theme.palette.secondary.main,
      background: theme.palette.secondary.main,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.secondary.main,
    },
    [CampaignStageGroupEnumType.Preparing]: {
      text: theme.palette.cyanA700,
      border: theme.palette.cyanA700,
      background: theme.palette.cyanA700,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.cyanA700,
    },
    [CampaignStageGroupEnumType.Scheduled]: {
      text: theme.palette.info.main,
      border: theme.palette.info.main,
      background: theme.palette.info.main,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.info.main,
    },
    [CampaignStageGroupEnumType.InProgress]: {
      text: theme.palette.greenA700,
      border: theme.palette.greenA700,
      background: theme.palette.greenA700,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.greenA700,
    },
    [CampaignStageGroupEnumType.Suspended]: {
      text: theme.palette.yellow800,
      border: theme.palette.yellow800,
      background: theme.palette.yellow800,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.yellow800,
    },
    [CampaignStageGroupEnumType.Completed]: {
      text: theme.palette.greenA900,
      border: theme.palette.greenA900,
      background: theme.palette.greenA900,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.greenA900,
    },
    [CampaignStageGroupEnumType.Canceled]: {
      text: theme.palette.error.main,
      border: theme.palette.error.main,
      background: theme.palette.error.main,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.error.main,
    },
  };

  const experimentStatusColors: Partial<
    Record<
      ExperimentStatus,
      {
        text: string;
        border: string;
        background: string;
        pillBackground: string;
        pillText: string;
      }
    >
  > = {
    [ExperimentStatus.Upcoming]: {
      text: theme.palette.neutral700,
      border: theme.palette.neutral700,
      background: theme.palette.neutral700,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.neutral700,
    },
    [ExperimentStatus.InProgress]: {
      text: theme.palette.lightGreen400,
      border: theme.palette.lightGreen400,
      background: theme.palette.green50,
      pillText: theme.palette.success.dark,
      pillBackground: theme.palette.lightGreen100,
    },
    [ExperimentStatus.Completed]: {
      text: theme.palette.greenA900,
      border: theme.palette.greenA900,
      background: theme.palette.greenA900,
      pillText: theme.palette.neutral00,
      pillBackground: theme.palette.greenA900,
    },
  };

  const segmentStatusColors: {
    [key in ContactSegmentGroupStatus]?: {
      text: string;
      border: string;
      background: string;
      pillBackground: string;
      pillText: string;
    };
  } = {
    [ContactSegmentGroupStatus.Review]: {
      text: theme.palette.neutral200,
      border: theme.palette.neutral200,
      background: theme.palette.neutral200,
      pillText: theme.palette.neutralA700,
      pillBackground: theme.palette.neutral200,
    },
    [ContactSegmentGroupStatus.Active]: {
      text: theme.palette.lightGreen400,
      border: theme.palette.lightGreen400,
      background: theme.palette.green50,
      pillText: theme.palette.success.dark,
      pillBackground: theme.palette.lightGreen100,
    },
  };

  const getStageGroup = (stage: CampaignStageType) => {
    const key = findKey(stageGroupMapping, (stages) => stages.includes(stage));
    // TODO [Nic] Properly type stageGroupMapping so that we can remove type casting below
    return key ? (key as CampaignStageGroupEnumType) : undefined;
  };

  const getStageLabel = (stage: CampaignStageType) => {
    const group = getStageGroup(stage);
    return labels[group!]!;
  };

  const getSubStageLabel = (stage: CampaignStageType) => {
    // const group = getStageGroup(stage);
    return subStageLabels[stage];
  };

  const getStageColor = (stage: CampaignStageType) => {
    const group = getStageGroup(stage);
    if (group) {
      return campaignStageColors[group];
    } else {
      return undefined;
    }
  };
  const getExperimentStatusColor = (experimentStatus: ExperimentStatus) => {
    if (experimentStatus) {
      return experimentStatusColors[experimentStatus];
    } else {
      return undefined;
    }
  };

  const getSegmentStatusColor = (
    segmentStatusStatus: ContactSegmentGroupStatus,
  ) => {
    if (segmentStatusStatus) {
      return segmentStatusColors[segmentStatusStatus];
    } else {
      return undefined;
    }
  };

  const isStageInDraftOrPreparing = (stage: CampaignStageType) =>
    [
      CampaignStageType.Draft,
      CampaignStageType.Syncing,
      CampaignStageType.ReadyToPrepareProofs,
      CampaignStageType.PreparingProofs,
      CampaignStageType.ReadyToExportProofs,
      CampaignStageType.ExportingProofs,
      CampaignStageType.ReadyToProof,
      CampaignStageType.Proofing,
      CampaignStageType.Proofed,
      CampaignStageType.ReadyToPrepare,
      CampaignStageType.Preparing,
      CampaignStageType.Prepared,
      CampaignStageType.ReadyToExport,
      CampaignStageType.Exporting,
      CampaignStageType.ReadyToSchedule,
    ].includes(stage);

  // Do not use this anymore. All stages should land on schedule tab
  const shouldStageLandOnProofsTab = (stage: CampaignStageType) =>
    [
      CampaignStageType.ReadyToProof,
      CampaignStageType.Proofed,
      CampaignStageType.Queuing,
      CampaignStageType.ReadyToSend,
      CampaignStageType.Sending,
      CampaignStageType.Completed,
      CampaignStageType.Scheduling,
      CampaignStageType.Scheduled,
      CampaignStageType.Unscheduling,
      CampaignStageType.ReadyToPrepare,
      CampaignStageType.Preparing,
      CampaignStageType.Prepared,
      CampaignStageType.ReadyToExport,
      CampaignStageType.Exporting,
      CampaignStageType.ReadyToSchedule,
      CampaignStageType.Suspending,
      CampaignStageType.Suspended,
      CampaignStageType.Resuming,
    ].includes(stage);

  return {
    getSegmentStatusColor,
    getExperimentStatusColor,
    getStageGroup,
    getStageLabel,
    getStageColor,
    getSubStageLabel,
    isStageInDraftOrPreparing,
    shouldStageLandOnProofsTab,
  };
};

export default useStageHelper;
